import { initWba, sendEvent, WbaEvents } from "@js/shared/libs";
import { Store } from "@store/index";

type TComponentModule = { default: new () => { init(store: Store): void | Promise<void> } };

const loadComponents = async (store: Store, loaders: Array<() => Promise<TComponentModule>>) => {
  const componentLoadResults = await Promise.allSettled(
    loaders.map((loader) => loader()),
  );

  const loadedComponents = componentLoadResults
    .filter((result): result is PromiseFulfilledResult<TComponentModule> => result.status === "fulfilled")
    .map((result) => new result.value.default());

  const componentInitResults = await Promise.allSettled(
    loadedComponents.map((component) => Promise.resolve(component.init(store))),
  );

  componentLoadResults.forEach((result, index) => {
    if (result.status === "rejected") {
      console.error(`Failed to load component #${index + 1}`, result.reason);
    }
  });

  componentInitResults.forEach((result, index) => {
    if (result.status === "rejected") {
      console.error(`Failed to init component #${index + 1}`, result.reason);
    }
  });
};

class App {
  constructor() {
    this.init = this.init.bind(this);
  }

  async init() {
    const store = new Store();

    // window.addEventListener(EUserEvents.LOG_IN, () => {
    //   syncWbaClientId(store.entities.user.info?.sub);
    // });

    // window.addEventListener(EUserEvents.LOG_OUT, () => {
    //   syncWbaClientId("0");
    // });

    try {
      const componentLoaders: Array<() => Promise<TComponentModule>> = [];

      if (document.querySelector(".ng-header")) {
        componentLoaders.push(() => import("@components/header"));
      }

      if (document.querySelector("[data-open-modal='application']")) {
        componentLoaders.push(() => import("@components/application"));
      }

      if (document.querySelector(".ng-hero")) {
        componentLoaders.push(() => import("@components/hero"));
      }

      if (document.querySelector(".ng-about")) {
        componentLoaders.push(() => import("@components/about"));
      }

      if (document.querySelector(".ng-track")) {
        componentLoaders.push(() => import("@components/track"));
      }

      if (document.querySelector(".ng-marquee")) {
        componentLoaders.push(() => import("@components/marquee"));
      }

      if (document.querySelector(".ng-player")) {
        componentLoaders.push(() => import("@components/player"));
      }

      if (document.querySelector(".ng-guide")) {
        componentLoaders.push(() => import("@components/guide"));
      }

      if (document.querySelector(".ng-nominations")) {
        componentLoaders.push(() => import("@components/nominations"));
      }

      if (document.querySelector(".ng-faq")) {
        componentLoaders.push(() => import("@components/faq"));
      }

      if (document.querySelector(".ng-partners")) {
        componentLoaders.push(() => import("@components/partners"));
      }

      if (document.querySelector(".ng-agreement")) {
        componentLoaders.push(() => import("@components/agreemet"));
      }

      if (document.querySelector(".ng-nebula")) {
        componentLoaders.push(() => import("@components/nebula"));
      }

      await loadComponents(store, componentLoaders);
    } catch (error) {
      console.error("Unexpected error during app initialization", error);
    }

    try {
      await store.init();
    } catch (error) {
      console.error("Unexpected error during store initialization", error);
    }

    if (window.location.hash !== "") {
      setTimeout(() => {
        const anchorElement = document.querySelector(window.location.hash);

        if (anchorElement !== null) {
          anchorElement.scrollIntoView({ block: "start", behavior: "instant" });
        }
      }, 0);
    }

    initWba();
    sendEvent(WbaEvents.PAGEVIEW);

    const params = new URLSearchParams(window.location.search);
    const utmSource = params.get("utm_source");
    const utmMedium = params.get("utm_medium");
    const utmCampaign = params.get("utm_campaign");

    if (utmSource && utmMedium && utmCampaign) {
      sendEvent(WbaEvents.NEWGENERATION_CAMPAIGN_OPEN, { utmSource, utmMedium, utmCampaign });
    }
  }
}

export { App };
